<template>
	<div class="publish-order-page">
		<section class="page-heading">
			<div class="heading-copy">
				<span class="heading-icon"><i class="el-icon-box"></i></span>
				<div>
					<h2>商品发布订单</h2>
					<p>查看当前账号发布商品时生成的订单及审核进度</p>
				</div>
			</div>
			<div class="heading-actions">
				<el-button v-if="orderList.length" size="small" plain icon="el-icon-document" @click="handleApplyInvoice">
					申请开票
				</el-button>
				<div class="heading-total">
					<span>订单总数</span>
					<strong>{{ pageOptions.total }}</strong>
				</div>
			</div>
		</section>

		<section class="order-filter-card">
			<div class="filter-caption">
				<span class="filter-icon"><i class="el-icon-search"></i></span>
				<strong>筛选订单</strong>
			</div>
			<el-form :model="queryParams" class="order-search-form">
				<el-form-item>
					<el-input v-model.trim="queryParams.orderCode" clearable placeholder="请输入订单编号"></el-input>
				</el-form-item>
				<el-form-item>
					<el-input v-model.trim="queryParams.goodsTitle" clearable placeholder="请输入商品名称"></el-input>
				</el-form-item>
				<el-form-item>
					<el-select v-model="queryParams.orderType" clearable placeholder="全部商品类型">
						<el-option v-for="item in orderTypes" :key="item.value" :label="item.label" :value="item.value"></el-option>
					</el-select>
				</el-form-item>
				<el-form-item>
					<el-select v-model="queryParams.auditState" clearable placeholder="全部审核状态">
						<el-option label="待财务审核" value="10"></el-option>
						<el-option label="待管理员审核" value="0"></el-option>
						<el-option label="审核通过" value="1"></el-option>
						<el-option label="审核不通过" value="2"></el-option>
					</el-select>
				</el-form-item>
				<div class="filter-actions">
					<el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
					<el-button icon="el-icon-refresh-left" @click="handleResetSearch">重置</el-button>
				</div>
			</el-form>
		</section>

		<section class="order-list-card">
			<div class="list-heading">
				<div>
					<strong>发布订单列表</strong>
					<span>仅展示当前账号发布商品时产生的订单</span>
				</div>
				<el-button size="small" icon="el-icon-refresh" @click="handleGetOrderList">刷新</el-button>
			</div>

			<div ref="orderList" v-loading="loading" class="order-list">
				<template v-if="orderList.length">
					<article v-for="order in orderList" :key="order.id" class="order-item">
						<header class="order-header">
							<div class="order-identification">
								<span :class="['order-type-tag', getOrderTypeClass(order.orderType)]">
									{{ getOrderTypeName(order.orderType) }}
								</span>
								<strong>订单号：{{ order.orderCode || '-' }}</strong>
								<span class="order-time">
									<i class="el-icon-time"></i>
									{{ order.createTime || '-' }}
								</span>
							</div>
							<div class="status-container">
								<span :class="['status-tag', getOrderStateClass(order.orderState)]">
									{{ getOrderStateName(order.orderState) }}
								</span>
								<span :class="['status-tag', getAuditStateClass(order.auditState)]">
									{{ getAuditStateName(order.auditState) }}
								</span>
								<span :class="['status-tag', getGoodsState(order.goodsRowState).className]">
									{{ getGoodsState(order.goodsRowState).text }}
								</span>
							</div>
						</header>

						<div class="order-content">
							<div class="product-info">
								<el-image v-if="order.mainImage" class="product-cover" :src="getGoodsImage(order)" fit="cover">
									<div slot="error" class="cover-placeholder"><i class="el-icon-picture-outline"></i></div>
								</el-image>
								<div v-else class="product-cover cover-placeholder"><i class="el-icon-goods"></i></div>
								<div class="product-copy">
									<strong>{{ order.goodsTitle || order.setMealName || '商品发布服务' }}</strong>
									<span>{{ getGoodsNatureName(order.goodsNature) }}</span>
									<small v-if="order.shopName">
										<i class="el-icon-office-building"></i>
										{{ order.shopName }}
									</small>
								</div>
							</div>

							<div class="order-facts">
								<div class="fact-item">
									<span>商品售价</span>
									<strong>¥ {{ formatAmount(order.sellingPrice) }}</strong>
								</div>
								<div class="fact-item">
									<span>支付方式</span>
									<strong>{{ getPaymentTypeName(order.paymentType) }}</strong>
								</div>
								<div class="fact-item amount-fact">
									<span>上架费用</span>
									<strong>¥ {{ formatAmount(order.actualPayment) }}</strong>
								</div>
							</div>
						</div>

						<footer class="order-footer">
							<div class="payment-time">
								<i :class="order.payTime ? 'el-icon-circle-check' : 'el-icon-warning-outline'"></i>
								<span>{{ order.payTime ? `支付时间：${order.payTime}` : '订单尚未支付' }}</span>
							</div>
							<div class="order-actions">
								<template v-if="isOrderUnpaid(order.orderState)">
									<el-button type="primary" icon="el-icon-wallet" @click="handleOpenPayVoucher(order)">立即付款</el-button>
									<el-button icon="el-icon-close" @click="handleCancelOrder(order)">取消订单</el-button>
								</template>
								<el-button icon="el-icon-document" @click="handleViewDetail(order)">订单详情</el-button>
								<el-popover
									v-model="order.deliveryProgressVisible"
									placement="left"
									width="360"
									trigger="click"
									popper-class="delivery-progress-popover"
								>
									<delivery-progress-card :order-id="order.id" :visible="order.deliveryProgressVisible" />
									<template #reference>
										<el-button icon="el-icon-time">订单进度</el-button>
									</template>
								</el-popover>
							</div>
						</footer>
					</article>
				</template>

				<empty-content v-else empty-text="暂无商品发布订单"></empty-content>

				<div v-if="pageOptions.total > pageOptions.limit" class="pagination-container">
					<jr-pagination
						:limit="pageOptions.limit"
						:page.sync="pageOptions.page"
						:total="pageOptions.total"
						layout="prev, pager, next"
						prev-text="上一页"
						next-text="下一页"
						@change="handlePaginationChange"
					></jr-pagination>
				</div>
			</div>
		</section>

		<jr-dialog :visible.sync="payVoucherVisible" width="1400px" :show-btn="false" @close="handlePayClose">
			<template slot="title">
				<h4 class="model-title"><span>上传付款凭证</span></h4>
			</template>
			<template slot="content">
				<upload-pay-voucher
					v-if="payVoucherVisible"
					:order-id="curPayOrderId"
					:order-number="curPayOrderNumber"
					@uploadSuccess="handlePaySuccessCallback"
					@uploadCancel="handlePayClose"
				></upload-pay-voucher>
			</template>
		</jr-dialog>

		<jr-dialog :visible.sync="showDetailDialog" :show-btn="false" width="1074px" custom-class="order-detail-dialog">
			<template slot="title">
				<h4 class="order-title"><span>订单详情</span></h4>
			</template>
			<template v-if="showDetailDialog" slot="content">
				<order-detail :id="selectedOrderId" :order="selectedOrder"></order-detail>
			</template>
		</jr-dialog>
	</div>
</template>

<script>
	import JrDialog from '@/components/Dialog'
	import JrPagination from '@/components/Pagination'
	import UploadPayVoucher from './components/uploadPayVoucher.vue'
	import DeliveryProgressCard from '../shoppmanag/components/DeliveryProgressCard.vue'
	import OrderDetail from '../shoppmanag/components/orderDetail.vue'
	import { cancelOrder } from '@/api/memberBenefit/index'
	import { previewMerchantFile, queryMyPublishedGoodsOrderPage } from '@/api/mall/index.js'
	import { ENUM_ORDER_STATE, ORDER_STATE, ORDER_STATE_CLASS } from '@/enum/index'

	const ORDER_TYPE_OPTIONS = [
		{ label: '自营商品', value: 'MERCHANT_SELF_GOODS' },
		{ label: '展销商品', value: 'MERCHANT_EXHIBITION_GOODS' }
	]

	const createQueryParams = () => ({
		orderCode: '',
		goodsTitle: '',
		orderType: '',
		auditState: ''
	})

	export default {
		name: 'PublishedGoodsOrderList',
		components: { DeliveryProgressCard, JrDialog, JrPagination, OrderDetail, UploadPayVoucher },
		data() {
			return {
				queryParams: createQueryParams(),
				pageOptions: { limit: 5, page: 1, total: 0 },
				loading: false,
				orderTypes: ORDER_TYPE_OPTIONS,
				orderList: [],
				payVoucherVisible: false,
				curPayOrderId: '',
				curPayOrderNumber: '',
				showDetailDialog: false,
				selectedOrderId: '',
				selectedOrder: {}
			}
		},
		created() {
			this.handleGetOrderList()
		},
		methods: {
			getOrderStateName(state) {
				return ENUM_ORDER_STATE[state] || '未知状态'
			},
			getOrderStateClass(state) {
				return ORDER_STATE_CLASS[state] || 'is-default'
			},
			getOrderTypeName(type) {
				const option = ORDER_TYPE_OPTIONS.find(item => item.value === type)
				return option ? option.label : '商品发布'
			},
			getOrderTypeClass(type) {
				return type === 'MERCHANT_EXHIBITION_GOODS' ? 'is-exhibition' : 'is-self'
			},
			getAuditStateName(state) {
				return { 0: '待管理员审核', 1: '审核通过', 2: '审核不通过', 10: '待财务审核' }[Number(state)] || '待审核'
			},
			getAuditStateClass(state) {
				return { 0: 'waitaudit', 1: 'passed', 2: 'rejected', 10: 'finance' }[Number(state)] || 'is-default'
			},
			getGoodsState(state) {
				return (
					{
						1: { text: '待上架', className: 'waitaudit' },
						7: { text: '草稿', className: 'is-default' },
						8: { text: '已上架', className: 'passed' },
						9: { text: '已下架', className: 'offline' }
					}[Number(state)] || { text: '待处理', className: 'is-default' }
				)
			},
			getGoodsNatureName(nature) {
				return Number(nature) === 2 ? '虚拟权益商品' : '实物商品'
			},
			getPaymentTypeName(type) {
				return { WECHAT_PAY: '微信支付', ALIPAY: '支付宝', BANK_AGGREGATE: '聚合码支付' }[type] || type || '-'
			},
			getGoodsImage(order) {
				const image = order.mainImage || ''
				if (!image || /^https?:\/\//.test(image) || image.indexOf('data:image') === 0) return image
				return previewMerchantFile() + image
			},
			formatAmount(value) {
				const amount = Number(value)
				return Number.isFinite(amount) ? amount.toFixed(2) : '0.00'
			},
			isOrderUnpaid(state) {
				return Number(state) === ORDER_STATE.UNPAID
			},
			async handleGetOrderList() {
				this.loading = true
				try {
					const response = await queryMyPublishedGoodsOrderPage({
						page: this.pageOptions.page,
						size: this.pageOptions.limit,
						...this.queryParams
					})
					const data = response.data || {}
					this.orderList = (data.records || []).map(item => ({
						...item,
						deliveryProgressVisible: false
					}))
					this.pageOptions.total = Number(data.total || 0)
					this.$nextTick(() => {
						if (this.$refs.orderList) this.$refs.orderList.scrollTop = 0
					})
				} catch (error) {
					this.orderList = []
					this.pageOptions.total = 0
				} finally {
					this.loading = false
				}
			},
			handlePaginationChange(pager) {
				this.pageOptions.page = pager.page
				this.pageOptions.limit = pager.limit
				this.handleGetOrderList()
			},
			handleSearch() {
				this.pageOptions.page = 1
				this.handleGetOrderList()
			},
			handleResetSearch() {
				this.queryParams = createQueryParams()
				this.pageOptions.page = 1
				this.pageOptions.limit = 5
				this.handleGetOrderList()
			},
			handleCancelOrder(order) {
				this.$modal
					.confirm('请确认是否取消该发布订单？')
					.then(() => cancelOrder({ orderId: order.id }))
					.then(() => {
						this.$modal.msgSuccess('订单取消成功')
						this.handleGetOrderList()
					})
					.catch(() => {})
			},
			handleApplyInvoice() {
				this.$router.push({ path: '/user/market/order/select' })
			},
			handleViewDetail(order) {
				this.selectedOrderId = order.id
				this.selectedOrder = { ...order }
				this.showDetailDialog = true
			},
			handleOpenPayVoucher(order) {
				this.curPayOrderId = order.id
				this.curPayOrderNumber = order.orderCode
				this.payVoucherVisible = true
			},
			handlePayClose() {
				this.curPayOrderId = ''
				this.curPayOrderNumber = ''
				this.payVoucherVisible = false
			},
			handlePaySuccessCallback() {
				this.handlePayClose()
				this.handleGetOrderList()
			}
		}
	}
</script>

<style lang="scss" scoped>
	.publish-order-page {
		display: flex;
		flex-direction: column;
		height: 100%;
		min-height: 0;
		padding: 12px 16px;
		overflow: hidden;
		color: #344154;
		background: #f5f8fb;
		box-sizing: border-box;
	}

	.page-heading,
	.order-filter-card,
	.order-list-card {
		border: 1px solid #e2e8ef;
		border-radius: 8px;
		background: #fff;
	}

	.page-heading,
	.heading-copy,
	.heading-actions,
	.order-filter-card,
	.filter-caption,
	.order-identification,
	.status-container,
	.product-info,
	.order-facts,
	.order-footer,
	.payment-time,
	.order-actions {
		display: flex;
		align-items: center;
	}

	.page-heading {
		justify-content: space-between;
		padding: 10px 16px;
	}

	.heading-copy {
		gap: 10px;

		h2 {
			margin: 0 0 3px;
			font-size: 18px;
			color: #303b4c;
		}

		p {
			margin: 0;
			font-size: 13px;
			color: #8b96a5;
		}
	}

	.heading-icon,
	.filter-icon {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		color: #2671cb;
		background: #edf5fe;
	}

	.heading-icon {
		width: 34px;
		height: 34px;
		border-radius: 7px;
		font-size: 18px;
	}

	.heading-actions {
		gap: 10px;
	}

	.heading-total {
		display: flex;
		align-items: baseline;
		gap: 8px;
		padding: 5px 11px;
		border: 1px solid #dce8f5;
		border-radius: 6px;
		color: #728093;
		background: #f7fafe;

		strong {
			color: #2671cb;
			font-size: 19px;
		}
	}

	.order-filter-card {
		gap: 12px;
		margin-top: 10px;
		padding: 8px 12px;
	}

	.filter-caption {
		flex: 0 0 116px;
		gap: 8px;
		padding-right: 12px;
		border-right: 1px solid #e6ebf1;
		font-size: 14px;
	}

	.filter-icon {
		width: 30px;
		height: 30px;
		border-radius: 7px;
	}

	.order-search-form {
		display: grid;
		flex: 1;
		grid-template-columns: minmax(150px, 1.2fr) minmax(150px, 1.2fr) repeat(2, minmax(140px, 1fr)) auto;
		align-items: center;
		gap: 8px;

		::v-deep .el-form-item {
			margin-bottom: 0;
		}

		::v-deep .el-input__inner {
			height: 32px;
			border-radius: 6px;
			line-height: 32px;
		}

		::v-deep .el-input__icon {
			line-height: 32px;
		}

		::v-deep .el-select {
			width: 100%;
		}
	}

	.filter-actions {
		white-space: nowrap;

		.el-button {
			height: 32px;
			padding: 0 13px;
		}
	}

	.order-list-card {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
		margin-top: 10px;
		padding: 12px 14px;
		overflow: hidden;
	}

	.list-heading {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 10px;
		padding-bottom: 9px;
		border-bottom: 1px solid #e7ecf2;

		strong {
			font-size: 16px;
		}

		span {
			margin-left: 10px;
			color: #98a2af;
			font-size: 12px;
		}
	}

	.order-list {
		flex: 1;
		min-height: 0;
		padding-right: 5px;
		overflow-x: hidden;
		overflow-y: auto;
		scrollbar-color: #b8cce0 #edf2f7;
		scrollbar-width: thin;

		&::-webkit-scrollbar {
			width: 7px;
		}

		&::-webkit-scrollbar-track {
			border-radius: 4px;
			background: #edf2f7;
		}

		&::-webkit-scrollbar-thumb {
			border-radius: 4px;
			background: #b8cce0;
		}
	}

	.order-item {
		margin-bottom: 12px;
		overflow: hidden;
		border: 1px solid #e2e8ef;
		border-radius: 8px;
		background: #fff;
		transition: border-color 0.2s, box-shadow 0.2s;

		&:hover {
			border-color: #bfd5ec;
			box-shadow: 0 6px 18px rgba(42, 78, 114, 0.08);
		}
	}

	.order-header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 8px 14px;
		border-bottom: 1px solid #e8edf3;
		background: #f8fafc;
	}

	.order-identification,
	.status-container {
		gap: 8px;
	}

	.order-identification strong {
		font-size: 13px;
		color: #3b485a;
	}

	.order-time {
		font-size: 12px;
		color: #8b96a4;

		i {
			margin-right: 4px;
		}
	}

	.order-type-tag,
	.status-tag {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: 24px;
		padding: 0 8px;
		border-radius: 6px;
		font-size: 12px;
		white-space: nowrap;
	}

	.order-type-tag.is-self {
		color: #287b62;
		background: #e6f5ee;
	}

	.order-type-tag.is-exhibition {
		color: #b06c1b;
		background: #fff1dc;
	}

	.status-tag {
		color: #6f7b89;
		background: #edf0f3;

		&.unpaid,
		&.waitaudit {
			color: #a97020;
			background: #fff2dc;
		}

		&.success,
		&.passed {
			color: #168157;
			background: #e6f5ed;
		}

		&.finance {
			color: #3970b7;
			background: #e8f1fd;
		}

		&.rejected,
		&.abolish {
			color: #c94d4d;
			background: #fdeaea;
		}

		&.offline,
		&.cancelled,
		&.is-default {
			color: #6f7b89;
			background: #edf0f3;
		}
	}

	.order-content {
		display: grid;
		grid-template-columns: minmax(320px, 1.35fr) minmax(390px, 1fr);
		align-items: center;
		gap: 18px;
		padding: 10px 14px;
	}

	.product-info {
		min-width: 0;
		gap: 12px;
	}

	.product-cover {
		flex: 0 0 68px;
		width: 68px;
		height: 52px;
		border-radius: 6px;
		background: #eef3f8;
	}

	.cover-placeholder {
		display: flex;
		align-items: center;
		justify-content: center;
		color: #95a8bc;
		font-size: 22px;
	}

	.product-copy {
		display: flex;
		min-width: 0;
		flex-direction: column;
		gap: 4px;

		strong,
		span,
		small {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		strong {
			font-size: 15px;
			color: #344154;
		}

		span,
		small {
			font-size: 12px;
			color: #8b96a5;
		}

		small i {
			margin-right: 4px;
		}
	}

	.order-facts {
		justify-content: flex-end;
	}

	.fact-item {
		display: flex;
		min-width: 116px;
		flex-direction: column;
		align-items: center;
		gap: 5px;
		padding: 0 18px;
		border-left: 1px solid #e7ecf2;

		span {
			font-size: 12px;
			color: #98a2af;
		}

		strong {
			font-size: 13px;
			color: #3c4858;
		}
	}

	.amount-fact strong {
		font-size: 17px;
		color: #d95f3a;
	}

	.order-footer {
		justify-content: space-between;
		min-height: 40px;
		padding: 5px 14px;
		border-top: 1px solid #e8edf3;
		background: #fbfcfd;
	}

	.payment-time {
		gap: 6px;
		font-size: 12px;
		color: #8793a2;

		i {
			color: #5aa87c;
		}
	}

	.order-actions {
		gap: 8px;

		.el-button {
			height: 30px;
			padding: 0 12px;
		}
	}

	.pagination-container {
		display: flex;
		justify-content: center;
		padding: 7px 0 2px;
	}

	@media (max-width: 1280px) {
		.order-search-form {
			grid-template-columns: repeat(2, minmax(150px, 1fr)) auto;
		}

		.order-content {
			grid-template-columns: 1fr;
		}

		.order-facts {
			justify-content: flex-start;
		}
	}

	@media (max-width: 900px) {
		.publish-order-page {
			height: auto;
			overflow: visible;
		}

		.order-filter-card,
		.page-heading,
		.order-header,
		.order-footer {
			align-items: flex-start;
			flex-direction: column;
		}

		.order-search-form {
			width: 100%;
			grid-template-columns: 1fr;
		}

		.filter-caption {
			border-right: 0;
		}

		.status-container {
			flex-wrap: wrap;
		}

		.order-list-card,
		.order-list {
			overflow: visible;
		}

		.order-facts {
			width: 100%;
			justify-content: space-between;
		}
	}
</style>

<style lang="scss">
	.delivery-progress-popover {
		padding: 0 !important;
		border: 1px solid #9ec0e4;
	}

	.order-detail-dialog {
		overflow: hidden;
		border: 1px solid #c7d9ec;
		border-radius: 8px;
		box-shadow: 0 12px 34px rgba(39, 80, 140, 0.16);

		.el-dialog__headerbtn {
			top: 10px;
			right: 16px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 32px;
			height: 32px;
			background: transparent;
			border-radius: 4px;

			.el-dialog__close {
				color: #667386;
				font-size: 20px;
				font-weight: 600;
			}

			&:hover {
				background: #eef2f6;

				.el-dialog__close {
					color: #263548;
				}
			}
		}

		.order-title {
			margin: 0;
			padding: 12px 48px 13px 0;
			border-bottom: 1px solid #e2e8ef;

			span {
				color: #344154;
				font-size: 18px;
			}
		}
	}
</style>
